import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { Calendar, Clock, Plus, ArrowLeft, Trash2, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import BlogPostCard from '@/components/BlogPostCard'
import ShareButtons from '@/components/ShareButtons'
import type {
  BlogPostCard as BlogPostCardType,
  BlogPostDetail,
  BlogCommentItem,
  SharedProps,
} from '@/types'

const ALLOWED_EMOJI = ['❤️', '👍', '😂', '😮', '😢', '🙏'] as const

type Props = {
  post: BlogPostDetail
  related: BlogPostCardType[]
  reactions_summary: Record<string, number>
  current_user_reaction: string | null
  comments: BlogCommentItem[]
  comments_enabled: boolean
  can_comment: boolean
  can_react: boolean
  is_preview?: boolean
}

export default function BlogShow({
  post,
  related,
  reactions_summary: reactionsSummary,
  current_user_reaction: currentUserReaction,
  comments,
  comments_enabled: commentsEnabled,
  can_comment: canComment,
  can_react: canReact,
  is_preview: isPreview = false,
}: Props) {
  const { app_url } = usePage<SharedProps>().props
  const canonical = `${app_url}/blog/${post.slug}`
  const showShare = !isPreview
  const ogImage = post.og_image_url || post.cover_image_url || undefined
  const bodyImageClass = isPreview
    ? 'prose-img:rounded-none prose-img:shadow-none'
    : 'prose-img:rounded-xl prose-img:shadow-md'

  return (
    <>
      <Head title={post.meta_title}>
        <meta head-key="description" name="description" content={post.meta_description} />
        <meta head-key="og:title" property="og:title" content={post.meta_title} />
        <meta head-key="og:description" property="og:description" content={post.meta_description} />
        <meta head-key="og:type" property="og:type" content="article" />
        <meta head-key="og:url" property="og:url" content={canonical} />
        {ogImage && <meta head-key="og:image" property="og:image" content={ogImage} />}
        <meta head-key="twitter:card" name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
        <link head-key="canonical" rel="canonical" href={canonical} />
        {post.published_at && (
          <meta head-key="article:published_time" property="article:published_time" content={post.published_at} />
        )}
        {post.edited_at && (
          <meta head-key="article:modified_time" property="article:modified_time" content={post.edited_at} />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: jsonLd({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: post.title,
              description: post.meta_description,
              image: ogImage ? [ogImage] : undefined,
              author: post.byline ? { '@type': 'Person', name: post.byline } : undefined,
              publisher: {
                '@type': 'Organization',
                name: 'Gala Season',
                url: app_url,
              },
              datePublished: post.published_at,
              dateModified: post.edited_at || post.published_at,
              mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
            }),
          }}
        />
      </Head>

      {isPreview && (
        <div className="sticky top-0 z-50 flex items-center justify-center gap-2 bg-amber-400 px-4 py-2 text-center text-sm font-medium text-amber-950">
          <Eye className="size-4 shrink-0" />
          <span>
            Preview — this is the last saved version and is only visible to admins. It is not published.
          </span>
        </div>
      )}

      <Hero post={post} showShare={showShare} shareUrl={canonical} shareTitle={post.title} />

      <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:py-16">
        <div
          className={`prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-semibold prose-headings:tracking-tight prose-a:text-primary ${bodyImageClass} prose-blockquote:border-primary/40 prose-pre:bg-foreground/95 prose-pre:text-background prose-th:border prose-th:border-black/10 prose-th:bg-muted/40 prose-th:px-3 prose-th:py-2 prose-td:border prose-td:border-black/10 prose-td:px-3 prose-td:py-2`}
          dangerouslySetInnerHTML={{ __html: post.body }}
        />

        {post.tags.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-muted/50 px-3 py-1 text-xs font-medium text-muted-foreground"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        <ReactionRow
          postSlug={post.slug}
          reactionsSummary={reactionsSummary}
          currentUserReaction={currentUserReaction}
          canReact={canReact}
          showShare={showShare}
          shareUrl={canonical}
          shareTitle={post.title}
        />

        {commentsEnabled && (
          <CommentsSection
            postSlug={post.slug}
            comments={comments}
            canComment={canComment}
          />
        )}
      </article>

      {related.length > 0 && (
        <section className="bg-muted/30 py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 font-serif text-2xl font-semibold tracking-tight">More from Run of Show</h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <BlogPostCard key={p.id} post={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}

function jsonLd(value: unknown) {
  return JSON.stringify(value).replace(/</g, '\\u003c')
}

function Hero({
  post,
  showShare,
  shareUrl,
  shareTitle,
}: {
  post: BlogPostDetail
  showShare: boolean
  shareUrl: string
  shareTitle: string
}) {
  const share = { showShare, shareUrl, shareTitle }
  if (post.cover_image_url) {
    return (
      <>
        <img
          src={post.cover_image_url}
          alt=""
          className="h-[40vh] min-h-[320px] w-full object-cover sm:h-[50vh]"
        />
        <div className="mx-auto max-w-4xl px-4 pt-8 sm:px-6 lg:pt-10">
          <BackToBlog className="text-muted-foreground hover:text-foreground" />
          <HeroMeta post={post} className="mt-6" subtle="text-muted-foreground" tone="light" {...share} />
        </div>
      </>
    )
  }
  return (
    <section className="bg-linear-to-br from-[oklch(0.25_0.02_50)] via-[oklch(0.35_0.10_46)] to-primary py-16 lg:py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <BackToBlog className="text-white/80 hover:text-white" />
        <HeroMeta post={post} className="mt-6 text-white" subtle="text-white/80" {...share} />
      </div>
    </section>
  )
}

function BackToBlog({ className }: { className?: string }) {
  return (
    <Link
      href="/blog"
      className={`inline-flex items-center gap-1 text-sm font-medium ${className || ''}`}
    >
      <ArrowLeft className="size-4" />
      Back to Run of Show
    </Link>
  )
}

function HeroMeta({
  post,
  className,
  subtle,
  tone = 'dark',
  showShare,
  shareUrl,
  shareTitle,
}: {
  post: BlogPostDetail
  className?: string
  subtle?: string
  tone?: 'dark' | 'light'
  showShare: boolean
  shareUrl: string
  shareTitle: string
}) {
  const badgeClass =
    tone === 'light'
      ? 'bg-primary/10 text-primary hover:bg-primary/20'
      : 'bg-white/15 text-white backdrop-blur-sm hover:bg-white/25'
  const shareTriggerClass =
    tone === 'light'
      ? 'w-auto'
      : 'w-auto border-white/40 bg-transparent text-white hover:bg-white/15 hover:text-white'
  return (
    <div className={className}>
      {post.category && (
        <Link
          href={`/blog/category/${post.category.slug}`}
          className={`inline-block rounded-full px-3 py-1 text-xs font-medium transition ${badgeClass}`}
        >
          {post.category.name}
        </Link>
      )}
      <h1 className="mt-4 font-serif text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
        {post.title}
      </h1>
      <div className={`mt-4 flex flex-wrap items-center gap-4 text-sm ${subtle || ''}`}>
        {post.byline && <span className="font-medium">{post.byline}</span>}
        {post.published_at && (
          <span className="flex items-center gap-1">
            <Calendar className="size-3.5" />
            {new Date(post.published_at).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            })}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Clock className="size-3.5" />
          {post.reading_time_minutes} min read
        </span>
      </div>
      {showShare && (
        <div className="mt-5">
          <ShareButtons
            title={shareTitle}
            url={shareUrl}
            label="Share"
            triggerClassName={shareTriggerClass}
          />
        </div>
      )}
    </div>
  )
}

function ReactionRow({
  postSlug,
  reactionsSummary,
  currentUserReaction,
  canReact,
  showShare,
  shareUrl,
  shareTitle,
}: {
  postSlug: string
  reactionsSummary: Record<string, number>
  currentUserReaction: string | null
  canReact: boolean
  showShare: boolean
  shareUrl: string
  shareTitle: string
}) {
  const [open, setOpen] = useState(false)
  const visibleEmoji = ALLOWED_EMOJI.filter((e) => (reactionsSummary[e] ?? 0) > 0)

  function react(emoji: string) {
    if (emoji === currentUserReaction) {
      router.delete(`/blog/${postSlug}/reaction`, { preserveScroll: true })
    } else {
      router.post(`/blog/${postSlug}/reaction`, { emoji }, { preserveScroll: true })
    }
    setOpen(false)
  }

  const showReactions = visibleEmoji.length > 0 || canReact
  if (!showReactions && !showShare) return null

  return (
    <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-black/5 pt-8">
      {showReactions && (
        <span className="mr-1 text-sm font-medium text-muted-foreground">React:</span>
      )}
      {visibleEmoji.map((emoji) => {
        const isMine = emoji === currentUserReaction
        return (
          <Button
            key={emoji}
            type="button"
            variant="outline"
            size="sm"
            disabled={!canReact}
            onClick={() => canReact && react(emoji)}
            className={`h-8 gap-1 rounded-full px-3 ${isMine ? 'border-primary/30 bg-primary/10' : ''}`}
            aria-pressed={isMine}
          >
            <span aria-hidden>{emoji}</span>
            <span className="font-medium tabular-nums">{reactionsSummary[emoji]}</span>
          </Button>
        )
      })}
      {canReact && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 rounded-full px-2 text-muted-foreground"
              aria-label="Add reaction"
            >
              <Plus className="size-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-1.5" align="start">
            <div className="flex gap-0.5">
              {ALLOWED_EMOJI.map((emoji) => (
                <Button
                  key={emoji}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => react(emoji)}
                  className={`h-9 w-9 rounded-md p-0 text-lg ${
                    emoji === currentUserReaction ? 'bg-primary/10' : ''
                  }`}
                >
                  {emoji}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
      {showReactions && !canReact && (
        <span className="ml-1 text-xs text-muted-foreground">
          <Link href="/users/sign_in" className="text-primary hover:underline">
            Sign in
          </Link>{' '}
          to react.
        </span>
      )}
      {showShare && (
        <div className="ml-auto">
          <ShareButtons title={shareTitle} url={shareUrl} label="Share" triggerClassName="w-auto" />
        </div>
      )}
    </div>
  )
}

function CommentsSection({
  postSlug,
  comments,
  canComment,
}: {
  postSlug: string
  comments: BlogCommentItem[]
  canComment: boolean
}) {
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!body.trim()) return
    setSubmitting(true)
    router.post(
      `/blog/${postSlug}/comments`,
      { comment: { body: body.trim() } },
      {
        preserveScroll: true,
        onFinish: () => {
          setSubmitting(false)
          setBody('')
        },
      },
    )
  }

  function deleteComment(id: string) {
    if (!confirm('Delete this comment?')) return
    router.delete(`/blog/${postSlug}/comments/${id}`, { preserveScroll: true })
  }

  return (
    <section className="mt-12 border-t border-black/5 pt-10">
      <h2 className="font-serif text-xl font-semibold tracking-tight">
        Discussion {comments.length > 0 && <span className="text-muted-foreground">({comments.length})</span>}
      </h2>

      {canComment ? (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <Textarea
            placeholder="Share your thoughts…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            maxLength={5000}
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={submitting || !body.trim()}>
              {submitting ? 'Posting…' : 'Post Comment'}
            </Button>
          </div>
        </form>
      ) : (
        <p className="mt-4 rounded-md bg-muted/30 p-4 text-sm text-muted-foreground">
          <Link href="/users/sign_in" className="font-medium text-primary hover:underline">
            Sign in
          </Link>{' '}
          to join the discussion.
        </p>
      )}

      {comments.length > 0 && (
        <ul className="mt-8 space-y-6">
          {comments.map((c) => (
            <li key={c.id} className="flex gap-3">
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarFallback>{c.user.name.charAt(0).toUpperCase() || '?'}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-baseline gap-2">
                    <Link
                      href={`/u/${c.user.username}`}
                      className="text-sm font-medium hover:underline"
                    >
                      {c.user.name}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {new Date(c.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                      {c.edited_at && ' · edited'}
                    </span>
                  </div>
                  {c.can_delete && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteComment(c.id)}
                      className="h-7 px-2 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  )}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/90">{c.body}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
